import { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import useModalStyles from "../../styles/Modals";
import useButtonStyles from "../../styles/Buttons";
import useSharedStyles from "../../styles/Shared";
import { getProjectSupplierFlaggedAnswers } from "../../api/Project";
import { notify, notifySimple } from "../../themes/components/Notifications";
import isEmpty from "lodash/isEmpty";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { InputAdornment, InputLabel, Typography } from "@mui/material";
import { COLORS } from "../../themes/Base";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "../Icons/FileDownloadIcon";
import ArrowDownIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpIcon from "@mui/icons-material/ArrowUpward";
import { orderBy } from "lodash";
import StickyPagination from "../../components/StickyPagination";
import { downloadFlaggedAnswers } from "../../api/Project";

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  projectSupplier: ProjectSupplier;
  baseName: string;
};

const AnswersModal: React.FC<Props> = ({ open, setOpen, projectSupplier, baseName }) => {
  if (!projectSupplier) return null;
  const { classes: modalClasses } = useModalStyles();
  const { classes: buttonClasses, cx } = useButtonStyles();
  const { classes: sharedClasses } = useSharedStyles();
  const [answers, setAnswers] = useState<ProjectSupplierAnswer[]>([]),
    [searchPhrase, setSearchPhrase] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("respondent_id:asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const searchedAnswers = useMemo(() => {
    if (isEmpty(searchPhrase) || answers.length === 0) {
      return answers;
    }

    // respondent_id starts with searchPhrase
    const startsWith = answers.filter(
      (answer) => answer.respondent_id.toLowerCase().startsWith(searchPhrase.toLowerCase())
    );

    // respondent_id contains searchPhrase (but does not start with)
    const contains = answers.filter(
      (answer) =>
        !answer.respondent_id.toLowerCase().startsWith(searchPhrase.toLowerCase()) &&
        answer.respondent_id.toLowerCase().includes(searchPhrase.toLowerCase())
    );

    // Other matches (title, answer, number)
    const others = answers.filter(
      (answer) =>
        !answer.respondent_id.toLowerCase().includes(searchPhrase.toLowerCase()) &&
        (answer.title.toLowerCase().includes(searchPhrase.toLowerCase()) ||
          answer.answer.toLowerCase().includes(searchPhrase.toLowerCase()) ||
          answer.number.toString().includes(searchPhrase))
    );

    // Combine all, keeping order: startsWith, contains, others
    return [
      ...orderBy(startsWith, ["respondent_id", "number"], ["asc", "asc"]),
      ...orderBy(contains, ["respondent_id", "number"], ["asc", "asc"]),
      ...orderBy(others, ["respondent_id", "number"], ["asc", "asc"]),
    ];
  }, [searchPhrase, answers]);

  const getAnswers = (): void => {
    try {
      getProjectSupplierFlaggedAnswers(projectSupplier.id)
        .then((response) => {
          const { result, messages } = response.ProjectSupplierFlaggedAnswers;
          if (!isEmpty(messages)) {
            notify(messages, "error");
          } else {
            if (result.length) {
              setAnswers(result);
            }
          }
        })
        .catch(() => {
          notifySimple(["Unexpected error occurred"], "error");
        });
    } catch (err) {
      notifySimple(["Unexpected error occurred"], "error");
    }
  };

  const [sortField, sortOrderValue] = sortBy.split(":");
  const processedAnswers = useMemo(() => {
    // sorting logic for all columns, including supplier name
    return orderBy(
      searchedAnswers,
      [sortField === "name" ? (el) => el.title?.toLowerCase().trim() : (el) => el[sortField]],
      [sortOrderValue as "asc" | "desc"]
    );
  }, [searchedAnswers, sortField, sortOrderValue]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortBy(`${field}:${sortOrderValue === "asc" ? "desc" : "asc"}`);
    } else {
      setSortBy(`${field}:asc`);
    }
  };

  const renderSortArrow = (field: string) => {
    if (sortField === field) {
      return sortOrderValue === "asc" ? <ArrowDownIcon /> : <ArrowUpIcon />;
    }
    return <ArrowDownIcon />;
  };

  const paginatedAnswers = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return processedAnswers.slice(start, start + perPage);
  }, [processedAnswers, currentPage, perPage]);

  const downloadXslx = () => {
    const options = {
      search: debouncedSearch || undefined,
      sortField: sortBy.split(":")[0],
      sortOrder: sortBy.split(":")[1],
    };
    downloadFlaggedAnswers(projectSupplier.id, options)
      .then((response) => {
        const downloadUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${baseName}_flagged_answers.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((e) => {
        notifySimple(["Failed to download file."], "error");
      });
  };

  useEffect(() => {
    if (open) {
      void getAnswers();
    }
  }, [projectSupplier, open, debouncedSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchPhrase);
    }, 400); // 400ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchPhrase]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => setOpen(false)}
      keepMounted={false}
      PaperProps={{
        className: sharedClasses.dialogModalBox,
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" sx={{ p: 0 }}>
            Answers - {projectSupplier?.source?.name}
            <span style={{ color: COLORS.textSecondary }}>
              &nbsp;{projectSupplier.country_code}
            </span>
          </Typography>
          <IconButton className={modalClasses.close} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box display="flex" justifyContent="space-between" gap={2} alignItems={"center"}>
          <Box className={sharedClasses.fieldBox} flex={1}>
            <InputLabel>Search</InputLabel>
            <TextField
              fullWidth
              placeholder="Search by Number, Title or Answer"
              onChange={(e) => setSearchPhrase(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ color: "#67676C", width: "20px" }} />
                  </InputAdornment>
                ),
                endAdornment: searchPhrase ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      edge="end"
                      size="small"
                      onClick={() => {
                        setSearchPhrase("");
                        setDebouncedSearch("");
                      }}
                    >
                      <ClearIcon sx={{ color: "#67676C", width: "20px" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
          <Button
            sx={{ mt: 1 }}
            className={cx(buttonClasses.blue, buttonClasses.iconButton)}
            disabled={processedAnswers.length === 0}
            onClick={() => downloadXslx()}
          >
            <FileDownloadIcon /> Download XLSX
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent className={modalClasses.content}>
        <Table id="AnswersTable" stickyHeader className={sharedClasses.responsesContentTable}>
          <TableHead>
            <TableRow>
              <TableCell
                className={sharedClasses.tableHeaderCell}
                onClick={() => handleSort("respondent_id")}
              >
                Respondent Id {renderSortArrow("respondent_id")}
              </TableCell>
              <TableCell
                className={sharedClasses.tableHeaderCell}
                onClick={() => handleSort("number")}
              >
                Number {renderSortArrow("number")}
              </TableCell>
              <TableCell
                className={sharedClasses.tableHeaderCell}
                onClick={() => handleSort("title")}
              >
                Title {renderSortArrow("title")}
              </TableCell>
              <TableCell
                className={sharedClasses.tableHeaderCell}
                onClick={() => handleSort("answer")}
              >
                Answer {renderSortArrow("answer")}
              </TableCell>
              <TableCell
                className={sharedClasses.tableHeaderCell}
                onClick={() => handleSort("response_types")}
              >
                Result {renderSortArrow("response_types")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAnswers.length > 0 ? (
              paginatedAnswers.map((answer, i) => (
                <TableRow key={`answer${i}`} className={modalClasses.tableRowContent}>
                  <TableCell>{answer.respondent_id}</TableCell>
                  <TableCell>{answer.number}</TableCell>
                  <TableCell>{answer.title}</TableCell>
                  <TableCell
                    style={{
                      minWidth: "100px",
                      maxWidth: "200px",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    <em>{answer.answer}</em>
                  </TableCell>
                  <TableCell
                    style={{
                      minWidth: "100px",
                      maxWidth: "200px",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {answer.response_types}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5}>No records found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      {
        processedAnswers.length > 0 && (
          <StickyPagination
            count={processedAnswers.length}
            page={currentPage}
            rowsPerPage={perPage}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={(n) => {
              setPerPage(n);
              setCurrentPage(1);
            }}
            sx={{ position: "sticky", width: "100%", px: 1 }}
          />
        )
      }
    </Dialog >
  );
};

export default AnswersModal;
